import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package, Edit2, Save, X, Search, AlertTriangle, Plus, Trash2 } from 'lucide-react';

interface InventoryItem {
  id: string;
  item_name: string;
  current_stock: number;
  reorder_point: number;
  unit?: string;
  category?: string;
}

export function InventoryManager() {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStock, setEditStock] = useState<number>(0);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .order('item_name', { ascending: true });

      if (error) throw error;
      setInventory(data || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    }
  };

  const handleEdit = (item: InventoryItem) => {
    setEditingId(item.id);
    setEditStock(item.current_stock);
  };

  const handleSave = async (id: string) => {
    try {
      const { error } = await supabase
        .from('inventory')
        .update({ current_stock: editStock })
        .eq('id', id);

      if (error) throw error;

      toast.success('Stock level updated');
      setEditingId(null);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditStock(0);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('inventory')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Item deleted');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const lowStockItems = inventory.filter(item => item.current_stock <= item.reorder_point);

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 rounded-xl border-slate-200 font-bold"
          />
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <p className="font-black uppercase">{lowStockItems.length} item(s) low on stock!</p>
          </div>
        </div>
      )}

      {/* Inventory List */}
      <div className="space-y-2">
        {filteredInventory.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-bold">No inventory items found</p>
            <p className="text-xs mt-1">Click "Add Item" to create your first inventory item</p>
          </div>
        ) : (
          filteredInventory.map((item) => (
            <div
              key={item.id}
              className={`bg-white border-2 rounded-2xl p-4 flex items-center justify-between ${
                item.current_stock <= item.reorder_point
                  ? 'border-red-200 bg-red-50'
                  : 'border-slate-100'
              }`}
            >
              <div className="flex-1">
                <p className="font-black text-slate-900">{item.item_name}</p>
                <p className="text-xs text-slate-500">
                  Reorder at: {item.reorder_point} {item.unit || 'units'}
                  {item.category && ` · ${item.category}`}
                </p>
              </div>

              {editingId === item.id ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={editStock}
                    onChange={(e) => setEditStock(parseFloat(e.target.value) || 0)}
                    className="w-24 h-10 rounded-xl"
                  />
                  <Button
                    onClick={() => handleSave(item.id)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="ghost"
                    size="sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className={`text-right ${
                    item.current_stock <= item.reorder_point
                      ? 'text-red-600'
                      : 'text-slate-900'
                  }`}>
                    <p className="text-2xl font-black">{item.current_stock}</p>
                    <p className="text-[10px] font-bold uppercase">{item.unit || 'units'}</p>
                  </div>
                  <Button
                    onClick={() => handleEdit(item)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(item.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onAdd={() => {
            fetchInventory();
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

// Add Item Modal Component
function AddItemModal({ onClose, onAdd }: { onClose: () => void; onAdd: () => void }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item_name: '',
    current_stock: '',
    reorder_point: '5',
    unit: 'units',
    category: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        setLoading(false);
        return;
      }

      const { error } = await supabase.from('inventory').insert([{
        user_id: user.id,
        item_name: formData.item_name,
        current_stock: parseFloat(formData.current_stock) || 0,
        reorder_point: parseFloat(formData.reorder_point) || 5,
        unit: formData.unit,
        category: formData.category || null,
        manager_notified: false,
      }]);

      if (error) throw error;

      toast.success(`${formData.item_name} added to inventory!`);
      onAdd();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full h-12 rounded-xl border-slate-200 font-bold focus:ring-2 ring-purple-500 outline-none";
  const labelClass = "text-[10px] font-black text-slate-500 uppercase mb-1 block";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full">
        <h3 className="text-xl font-black text-slate-900 uppercase mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-600" />
          Add Inventory Item
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Item Name *</label>
            <Input
              value={formData.item_name}
              onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              className={inputClass}
              placeholder="e.g. Car Shampoo 20L"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Current Stock *</label>
              <Input
                type="number"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                className={inputClass}
                placeholder="0"
                required
              />
            </div>
            <div>
              <label className={labelClass}>Reorder Point</label>
              <Input
                type="number"
                value={formData.reorder_point}
                onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                className={inputClass}
                placeholder="5"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Unit</label>
            <select
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className={inputClass}
            >
              <option value="units">Units</option>
              <option value="L">Litres (L)</option>
              <option value="mL">Millilitres (mL)</option>
              <option value="kg">Kilograms (kg)</option>
              <option value="g">Grams (g)</option>
              <option value="m">Metres (m)</option>
              <option value="pcs">Pieces</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Category (Optional)</label>
            <Input
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className={inputClass}
              placeholder="e.g. Cleaning, Paint, Tools"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
            >
              {loading ? 'Adding...' : 'Add to Inventory'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-2xl"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
